'use client';
import React, { useEffect, useState, useMemo } from 'react';
import { faBolt, faRotate, faUserTie, faTag } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { useAuth } from 'src/context/auth';
import { useDashboard } from 'src/context/providers/DashboardProvider';
import {
  useGetCompanyUsersQuery,
  useGetObjectDefinitionsListQuery,
  useGetFieldDefinitionsQuery,
  useGetActivityTypesQuery,
  useGetActivityCategoriesQuery,
} from 'src/api/redux';
import { Search } from 'src/components';
import RadioButtonSelect from './components/RadioButtonSelect';
import { MultiSelectMenu } from './components/MultiSelectMenu';
import { SegmentedMultiSelectMenu } from './components/SegmentedMultiSelectMenu';
import { useDebounce } from 'use-debounce';

const FilterWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="flex flex-col items-start gap-1 w-40 min-w-[120px]">
    {children}
  </div>
);
interface ActivityTableToolbarProps {
  onSearch: (value: string) => void;
  selectedReps: string[];
  setSelectedReps: any;
  selectedActivities: string[];
  setSelectedActivities: any;
  selectedStatuses: {
    [key: string]: string[];
  };
  setSelectedStatuses: any;
  selectedTimeframe: string;
  setSelectedTimeframe: any;
  statuses: { crm_type: string; crm_status: string }[];
  selectedCategories?: string[];
  setSelectedCategories?: any;
  showCategoryFilter?: boolean;
  categoryLabel?: string;
}

const ActivityTableToolbar = ({
  onSearch,
  selectedReps,
  setSelectedReps,
  selectedActivities,
  setSelectedActivities,
  selectedStatuses,
  setSelectedStatuses,
  selectedTimeframe,
  setSelectedTimeframe,
  statuses,
  selectedCategories,
  setSelectedCategories,
  showCategoryFilter = false,
  categoryLabel = 'Category',
}: ActivityTableToolbarProps) => {
  const { user } = useAuth();
  const companyId = user.company.id.toString();
  const isManager = user?.permissions?.is_manager || user.is_superuser;

  // Local search state for immediate UI feedback
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);

  // Notify parent when debounced value changes
  useEffect(() => {
    onSearch(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  const { data: users, isLoading } = useGetCompanyUsersQuery({ companyId });
  const totalRepCount = users && users?.count;
  const { data: allActivities } = useGetActivityTypesQuery();
  const activityList = allActivities?.results;
  const totalActivityCount = allActivities && allActivities?.count;

  const { data: allCategories } = useGetActivityCategoriesQuery(undefined, {
    skip: !showCategoryFilter,
  });
  const categoryList = useMemo(() => {
    if (!allCategories?.results?.length) return [];
    return allCategories.results
      .filter((c) => !c.deactivated)
      .map((c) => ({ id: c.id, label: c.label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allCategories]);
  const { userQueryParams, setUserQueryParams } = useDashboard();
  const { pageSize, page } = userQueryParams;

  // Fetch field definitions to get status labels
  const { data: objectDefinitions } = useGetObjectDefinitionsListQuery();
  const { data: fieldDefinitions } = useGetFieldDefinitionsQuery(
    {
      objectId: objectDefinitions?.results?.[0]?.id,
    },
    {
      skip: !objectDefinitions?.results?.[0]?.id,
    }
  );

  // Create a lookup map from status value to label from field definitions
  const statusLabelMap = useMemo(() => {
    const statusField = fieldDefinitions?.results?.find(
      (field) => field?.label?.toLowerCase() === 'status'
    );
    if (!statusField?.options) return new Map<string, string>();
    return new Map(statusField.options.map((opt) => [opt.value, opt.label]));
  }, [fieldDefinitions]);

  useEffect(() => {
    return () => {
      setUserQueryParams({
        q: '',
        page,
        pageSize: 50,
        sort: '',
        order: '',
      });
    };
  }, []);

  const onMenuScroll = (isActivity: boolean) => {
    const totalCount = isActivity ? totalActivityCount : totalRepCount;
    if (totalCount > pageSize) {
      setUserQueryParams({ ...userQueryParams, pageSize: pageSize + 50 });
    }
  };

  const representativeOptions =
    users &&
    users?.results?.map((report) => {
      const fullName = `${report.first_name} ${report.last_name}`;
      return { id: report.id, label: fullName };
    });

  const statusOptions = useMemo(() => {
    return statuses?.reduce((acc, status) => {
      const { crm_type, crm_status } = status;
      if (!crm_status) {
        return acc;
      }
      if (!acc[crm_type]) {
        acc[crm_type] = [];
      }
      // Look up label from field definitions, fall back to crm_status
      const label = statusLabelMap.get(crm_status) || crm_status;
      acc[crm_type].push({ label, id: crm_status });
      return acc;
    }, {} as Record<string, { label: string; id: string }[]>);
  }, [statuses, statusLabelMap]);

  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean;
  }>({});

  const handleTimeframeChange = (value: string) => {
    if (value === 'all') {
      setSelectedTimeframe();
    } else {
      setSelectedTimeframe(value);
    }
  };

  const setSelectedOptions = (
    optionType: string,
    options?: string[] | { crm_type: string; crm_status: string }[]
  ) => {
    if (optionType === 'reps') {
      setSelectedReps(options);
    }
    if (optionType === 'activities') {
      setSelectedActivities(options);
    }
    if (optionType === 'categories') {
      setSelectedCategories(options);
    }
  };

  const handleStatusSelection = (
    _optionType: string,
    options: { [key: string]: string[] }
  ) => {
    setSelectedStatuses(options);
  };

  const hasStatuses = statuses?.some((obj) => obj.crm_status != null);

  return (
    <div className="flex px-4 py-3">
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-2 w-full">
          <div
            className="flex flex-col gap-1 min-w-[17rem]"
            style={{ flex: 0.2 }}
          >
            <label
              htmlFor="search-input"
              className="self-stretch justify-start text-primary font-semibold font-['Inter'] leading-tight text-xs"
            >
              Search Targets
            </label>
            <Search
              size="sm"
              id="activity-search-input"
              value={searchTerm}
              onSearch={setSearchTerm}
              placeholder="Find a Target by name"
              className="rounded-full"
            />
          </div>
          {isManager ? (
            <FilterWrapper>
              <label
                htmlFor="rep-name-select"
                className="self-stretch justify-start text-primary font-semibold font-['Inter'] leading-tight text-xs"
              >
                Rep. Name
              </label>
              <MultiSelectMenu
                id="activity-rep-name-select"
                header="Filter by Rep"
                optionType="reps"
                options={representativeOptions}
                enableSearch={true}
                selectedOptions={selectedReps}
                setSelectedOptions={setSelectedOptions}
                startDecorator={
                  <Icon icon={faUserTie} className="text-secondary" />
                }
                onMenuScroll={onMenuScroll}
                isLoading={isLoading}
              />
            </FilterWrapper>
          ) : null}
          <FilterWrapper>
            <label
              htmlFor="activity-type-select"
              className="self-stretch justify-start text-primary font-semibold font-['Inter'] leading-tight text-xs"
            >
              Type
            </label>
            <MultiSelectMenu
              id="activity-type-select"
              header="Filter by Type"
              optionType="activities"
              options={activityList}
              isActivity={true}
              startDecorator={<Icon icon={faBolt} className="text-secondary" />}
              selectedOptions={selectedActivities}
              setSelectedOptions={setSelectedOptions}
              isLoading={isLoading}
              onMenuScroll={onMenuScroll}
            />
          </FilterWrapper>
          {showCategoryFilter && (
            <FilterWrapper>
              <label
                htmlFor="activity-category-select"
                className="self-stretch justify-start text-primary font-semibold font-['Inter'] leading-tight text-xs"
              >
                {categoryLabel}
              </label>
              <MultiSelectMenu
                id="activity-category-select"
                header={`Filter by ${categoryLabel}`}
                optionType="categories"
                options={categoryList}
                startDecorator={
                  <Icon icon={faTag} className="text-secondary" />
                }
                selectedOptions={selectedCategories}
                setSelectedOptions={setSelectedOptions}
                isLoading={isLoading}
                onMenuScroll={onMenuScroll}
              />
            </FilterWrapper>
          )}
          {hasStatuses && (
            <FilterWrapper>
              <label
                htmlFor="activity-status-select"
                className="self-stretch justify-start text-primary font-semibold font-['Inter'] leading-tight text-xs"
              >
                Status
              </label>
              <SegmentedMultiSelectMenu
                filterHeader="Filter by Status"
                optionType="statuses"
                optionData={statusOptions}
                testId="activity-status-select"
                startDecorator={
                  <Icon icon={faRotate} className="text-secondary" />
                }
                selectedOptions={selectedStatuses}
                setSelectedOptions={handleStatusSelection}
                isLoading={isLoading}
                onMenuScroll={onMenuScroll}
                expandedSections={expandedSections}
                setExpandedSections={setExpandedSections}
              />
            </FilterWrapper>
          )}
          <FilterWrapper>
            <label
              htmlFor="activity-timeframe-select"
              className="self-stretch justify-start text-primary font-semibold font-['Inter'] leading-tight text-xs"
            >
              Timeframe
            </label>
            <RadioButtonSelect
              id="timeframe-select"
              header="Timeframe"
              selectOnChange={handleTimeframeChange}
              selectedOption={selectedTimeframe || 'all'}
            />
          </FilterWrapper>
        </div>
      </div>
    </div>
  );
};

export default ActivityTableToolbar;
